import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Shield, ArrowLeft, BookOpen, Clock, AlertTriangle, CheckCircle2,
  Users, Calendar, Loader2, UserPlus, RefreshCw, XCircle, HelpCircle,
  ExternalLink, ChevronDown, ChevronUp, Brain, Sparkles, Zap,
  BarChart3, Target, TrendingUp, Filter, Search
} from "lucide-react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Progress } from "../../components/ui/progress";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "../../components/ui/tooltip";
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer,
} from "recharts";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import AssignmentModal from "../../components/shared/AssignmentModal";

const STATUS_STYLES = {
  completed: "bg-green-50 text-green-700 border-green-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  in_progress: "bg-blue-50 text-blue-700 border-blue-200",
  overdue: "bg-red-50 text-red-700 border-red-200",
  not_enrolled: "bg-slate-50 text-slate-500 border-slate-200",
  enrolled: "bg-blue-50 text-blue-600 border-blue-200",
};

const SEVERITY_STYLE = {
  critical: { bg: "bg-red-50", border: "border-red-200", text: "text-red-700", dot: "bg-red-500" },
  warning: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", dot: "bg-amber-500" },
  success: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", dot: "bg-emerald-500" },
  info: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", dot: "bg-blue-500" },
};

const StandingTable = ({ assignments, overdue, pending, completed, expandedUser, setExpandedUser, navigate, setShowAssign, fetchData }) => (
  <Card className="border shadow-none" data-testid="standing-table">
    <CardHeader className="pb-2 pt-4 px-5">
      <div className="flex items-center justify-between">
        <CardTitle className="text-sm flex items-center gap-2">
          <Users className="w-4 h-4 text-primary" /> User Standing ({assignments.length})
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <HelpCircle className="w-3.5 h-3.5 text-muted-foreground/50 cursor-help" data-testid="standing-help" />
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-xs text-xs leading-relaxed">
                <p className="font-semibold mb-1">What is User Standing?</p>
                <p>Shows each assigned user's compliance status. A user is <span className="text-green-600 font-medium">Compliant</span> when they complete all required courses. <span className="text-amber-600 font-medium">Pending</span> means courses are still in progress. <span className="text-red-600 font-medium">Overdue</span> means the deadline has passed without completion.</p>
                <p className="mt-1">Click on a user row to see their individual course-by-course progress.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </CardTitle>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={fetchData}><RefreshCw className="w-3 h-3 mr-1" /> Refresh</Button>
      </div>
    </CardHeader>
    <CardContent className="px-5 pb-4">
      {assignments.length === 0 ? (
        <div className="text-center py-8">
          <Users className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" />
          <p className="text-sm text-muted-foreground mb-3">No users assigned yet</p>
          <Button size="sm" onClick={() => setShowAssign(true)}><UserPlus className="w-4 h-4 mr-1" /> Assign Users</Button>
        </div>
      ) : (
        <div className="space-y-1">
          {[...overdue, ...pending, ...completed].map((a) => {
            const isExpanded = expandedUser === a.id;
            const courseProgressPct = a.total_courses > 0 ? Math.round((a.completed_courses / a.total_courses) * 100) : 0;
            return (
              <motion.div key={a.id} initial={{ opacity: 0, y: 3 }} animate={{ opacity: 1, y: 0 }}>
                <div className={`rounded-lg border transition-colors ${isExpanded ? "border-primary/30 bg-primary/[0.02]" : "hover:bg-muted/30"}`} data-testid={`standing-${a.id}`}>
                  <div className="flex items-center gap-3 p-2.5 cursor-pointer" onClick={() => setExpandedUser(isExpanded ? null : a.id)}>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold shrink-0 ${a.status === "completed" ? "bg-green-100 text-green-700" : a.status === "overdue" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                      {a.user_name?.charAt(0) || "?"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium truncate">{a.user_name || a.user_id}</p>
                        {a.user_department && <span className="text-[9px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{a.user_department}</span>}
                      </div>
                      <p className="text-[10px] text-muted-foreground">Due: {new Date(a.due_date).toLocaleDateString()} · Assigned: {new Date(a.assigned_at).toLocaleDateString()}</p>
                    </div>
                    {a.total_courses > 0 && (
                      <div className="flex items-center gap-2 shrink-0">
                        <div className="w-16"><Progress value={courseProgressPct} className="h-1.5" /></div>
                        <span className="text-[10px] font-medium text-muted-foreground w-16 text-right">{a.completed_courses}/{a.total_courses} courses</span>
                      </div>
                    )}
                    <Badge className={`text-[10px] shrink-0 ${STATUS_STYLES[a.status] || ""}`}>{a.status}</Badge>
                    {a.status === "completed" && a.completed_at && <span className="text-[10px] text-muted-foreground shrink-0">Completed {new Date(a.completed_at).toLocaleDateString()}</span>}
                    {a.total_courses > 0 && (isExpanded ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground shrink-0" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />)}
                  </div>
                  {isExpanded && a.course_progress && a.course_progress.length > 0 && (
                    <div className="px-3 pb-3 pt-0">
                      <div className="ml-11 space-y-1.5 border-t pt-2.5">
                        <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Course-by-Course Progress</p>
                        {a.course_progress.map((cp) => (
                          <div key={cp.course_id} className="flex items-center gap-2.5 py-1.5 px-2 rounded-md hover:bg-muted/50 cursor-pointer transition-colors" onClick={(e) => { e.stopPropagation(); navigate(`/course/${cp.course_id}`); }} data-testid={`course-progress-${cp.course_id}`}>
                            <div className={`w-5 h-5 rounded flex items-center justify-center shrink-0 ${cp.status === "completed" ? "bg-green-100" : cp.status === "in_progress" ? "bg-blue-100" : cp.enrolled ? "bg-amber-100" : "bg-slate-100"}`}>
                              {cp.status === "completed" ? <CheckCircle2 className="w-3 h-3 text-green-600" /> : cp.status === "in_progress" ? <Clock className="w-3 h-3 text-blue-600" /> : <BookOpen className="w-3 h-3 text-muted-foreground" />}
                            </div>
                            <span className="text-xs flex-1 truncate">{cp.course_title}</span>
                            {cp.enrolled && cp.status !== "completed" && (
                              <div className="flex items-center gap-1.5"><div className="w-12"><Progress value={cp.progress_percent} className="h-1" /></div><span className="text-[10px] text-muted-foreground w-7 text-right">{cp.progress_percent}%</span></div>
                            )}
                            <Badge className={`text-[9px] py-0 px-1.5 ${STATUS_STYLES[cp.status] || STATUS_STYLES.not_enrolled}`}>{cp.status === "not_enrolled" ? "Not Started" : cp.status.replace("_", " ")}</Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </CardContent>
  </Card>
);

const ComplianceDetailPage = () => {
  const { complianceId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssign, setShowAssign] = useState(false);
  const [expandedUser, setExpandedUser] = useState(null);
  const [activeTab, setActiveTab] = useState("standing");
  const [compAnalytics, setCompAnalytics] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const basePath = user?.role === "super_admin" ? "/admin" : "/tenant";
  const variant = user?.role === "super_admin" ? "super_admin" : "tenant_admin";

  useEffect(() => { fetchData(); }, [complianceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await api.get(`/compliance/templates/${complianceId}/analytics`);
      setCompAnalytics(res.data);
    } catch (e) { console.error(e); }
  }, [complianceId]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [tRes, aRes, cRes] = await Promise.all([
        api.get(`/compliance/templates/${complianceId}`),
        api.get(`/compliance/assignments?template_id=${complianceId}`),
        api.get("/courses"),
      ]);
      setTemplate(tRes.data);
      setAssignments(aRes.data || []);
      const c = cRes.data?.courses || cRes.data || [];
      setCourses(Array.isArray(c) ? c : []);
    } catch (e) {
      toast.error("Failed to load compliance");
      navigate(`${basePath}/compliances`);
    }
    finally { setLoading(false); }
  };

  if (loading) return (
    <AdminLayout title="Compliance" variant={variant}>
      <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
    </AdminLayout>
  );

  if (!template) return null;

  const completed = assignments.filter(a => a.status === "completed");
  const pending = assignments.filter(a => ["pending", "in_progress"].includes(a.status));
  const overdue = assignments.filter(a => a.status === "overdue");
  const rate = assignments.length > 0 ? Math.round(completed.length / assignments.length * 100) : 0;
  const linkedCourses = (template.course_ids || []).map(id => courses.find(c => c.id === id)).filter(Boolean);

  const todos = [];
  if (overdue.length > 0) todos.push({ type: "overdue", text: `${overdue.length} user${overdue.length > 1 ? "s are" : " is"} overdue`, severity: "high" });
  if (pending.length > 0 && pending.some(a => { const d = new Date(a.due_date); const now = new Date(); return (d - now) / 86400000 < 7; })) {
    const urgent = pending.filter(a => { const d = new Date(a.due_date); const now = new Date(); return (d - now) / 86400000 < 7; });
    todos.push({ type: "urgent", text: `${urgent.length} user${urgent.length > 1 ? "s have" : " has"} deadlines within 7 days`, severity: "medium" });
  }
  if (assignments.length === 0) todos.push({ type: "unassigned", text: "No users assigned yet -- assign to people, groups, or departments", severity: "low" });
  if (linkedCourses.length === 0) todos.push({ type: "no_courses", text: "No courses attached -- edit to add required courses", severity: "low" });
  if (rate === 100 && assignments.length > 0) todos.push({ type: "complete", text: "All users are compliant!", severity: "done" });

  return (
    <AdminLayout title={template.title} variant={variant}>
      <div className="space-y-5" data-testid="compliance-detail-page">
        {/* Back + Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(`${basePath}/compliances`)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold tracking-tight" data-testid="compliance-name">{template.title}</h1>
              {template.is_mandatory && <Badge className="bg-red-50 text-red-600 border-red-200 text-[10px]">Mandatory</Badge>}
              {template.recurrence && <Badge variant="outline" className="text-[10px] capitalize">{template.recurrence.replace("_", " ")}</Badge>}
            </div>
            {template.description && <p className="text-sm text-muted-foreground mt-0.5">{template.description}</p>}
          </div>
          <Button onClick={() => setShowAssign(true)} data-testid="assign-compliance-btn">
            <UserPlus className="w-4 h-4 mr-1" /> Assign
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[
            { label: "Total Assigned", value: assignments.length, icon: Users, color: "#3b82f6", bg: "bg-blue-50" },
            { label: "Compliant", value: completed.length, icon: CheckCircle2, color: "#22c55e", bg: "bg-green-50" },
            { label: "Pending", value: pending.length, icon: Clock, color: "#f59e0b", bg: "bg-amber-50" },
            { label: "Overdue", value: overdue.length, icon: AlertTriangle, color: "#ef4444", bg: "bg-red-50" },
            { label: "Compliance Rate", value: `${rate}%`, icon: Shield, color: "#8b5cf6", bg: "bg-violet-50" },
          ].map((st, i) => (
            <Card key={i} className="border shadow-none">
              <CardContent className="p-3.5 flex items-center gap-3">
                <div className={`w-9 h-9 rounded-lg ${st.bg} flex items-center justify-center`}>
                  <st.icon className="w-4 h-4" style={{ color: st.color }} />
                </div>
                <div>
                  <p className="text-xl font-bold leading-none">{st.value}</p>
                  <p className="text-[10px] text-muted-foreground">{st.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Overall Progress */}
        <Card className="border shadow-none">
          <CardContent className="py-4 px-5">
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-sm">Overall Compliance</span>
              <span className="text-xl font-bold text-primary">{rate}%</span>
            </div>
            <Progress value={rate} className="h-2.5" />
            <div className="flex items-center justify-between mt-1.5 text-[10px] text-muted-foreground">
              <span>{completed.length} of {assignments.length} users compliant</span>
              <span>Due within {template.due_days} days of assignment</span>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Required Courses - Now Clickable */}
          <Card className="border shadow-none lg:col-span-1">
            <CardHeader className="pb-2 pt-4 px-5">
              <CardTitle className="text-sm flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-primary" /> Required Courses ({linkedCourses.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="px-5 pb-4 space-y-2">
              {linkedCourses.length === 0 ? (
                <p className="text-center py-6 text-sm text-muted-foreground">No courses attached</p>
              ) : linkedCourses.map(c => (
                <div
                  key={c.id}
                  className="flex items-center gap-2.5 p-2.5 rounded-lg border hover:border-primary/40 hover:bg-primary/5 cursor-pointer transition-all group"
                  onClick={() => navigate(`/course/${c.id}`)}
                  data-testid={`course-link-${c.id}`}
                >
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <BookOpen className="w-3.5 h-3.5 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium truncate group-hover:text-primary transition-colors">{c.title}</p>
                    <p className="text-[9px] text-muted-foreground">{c.category || "General"} · {c.duration_hours || 0}h</p>
                  </div>
                  <ExternalLink className="w-3.5 h-3.5 text-muted-foreground/40 group-hover:text-primary transition-colors shrink-0" />
                </div>
              ))}
            </CardContent>
          </Card>

          {/* To-Do List */}
          <Card className="border shadow-none lg:col-span-2">
            <CardHeader className="pb-2 pt-4 px-5">
              <CardTitle className="text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" /> Action Items</CardTitle>
            </CardHeader>
            <CardContent className="px-5 pb-4 space-y-2">
              {todos.length === 0 ? (
                <p className="text-center py-6 text-sm text-muted-foreground">No action items</p>
              ) : todos.map((todo, i) => (
                <div key={i} className={`flex items-center gap-3 p-3 rounded-lg border ${
                  todo.severity === "high" ? "border-red-200 bg-red-50/50" :
                  todo.severity === "medium" ? "border-amber-200 bg-amber-50/50" :
                  todo.severity === "done" ? "border-green-200 bg-green-50/50" :
                  "border-slate-200 bg-slate-50/50"
                }`} data-testid={`todo-${i}`}>
                  {todo.severity === "high" ? <XCircle className="w-4 h-4 text-red-500 shrink-0" /> :
                   todo.severity === "medium" ? <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" /> :
                   todo.severity === "done" ? <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" /> :
                   <Clock className="w-4 h-4 text-slate-400 shrink-0" />}
                  <p className="text-sm flex-1">{todo.text}</p>
                  {todo.type === "overdue" && <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => setShowAssign(true)}>Send Reminder</Button>}
                  {todo.type === "unassigned" && <Button size="sm" className="text-xs h-7" onClick={() => setShowAssign(true)}>Assign Now</Button>}
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Analytics Tabs */}
        <div className="flex gap-1 p-1 bg-muted/50 rounded-xl w-fit" data-testid="compliance-tabs">
          {[
            { id: "standing", label: "User Standing", icon: Users },
            { id: "analytics", label: "Analytics", icon: BarChart3 },
            { id: "ai", label: "AI Insights", icon: Brain },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === tab.id ? "bg-white shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
              data-testid={`tab-${tab.id}`}>
              <tab.icon className="w-4 h-4" />{tab.label}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {activeTab === "standing" && (
            <motion.div key="standing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              {/* Standing table is displayed here */}
              <StandingTable
                assignments={assignments} overdue={overdue} pending={pending} completed={completed}
                expandedUser={expandedUser} setExpandedUser={setExpandedUser} navigate={navigate}
                setShowAssign={setShowAssign} fetchData={fetchData}
              />
            </motion.div>
          )}

          {activeTab === "analytics" && compAnalytics && (
            <motion.div key="analytics" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Status Pie */}
                <Card>
                  <CardContent className="p-5">
                    <h3 className="text-sm font-bold mb-4 flex items-center gap-2"><Shield className="w-4 h-4 text-primary" /> Compliance Status</h3>
                    <div className="flex items-center gap-6">
                      <ResponsiveContainer width={160} height={160}>
                        <PieChart>
                          <Pie data={[
                            { name: "Compliant", value: compAnalytics.summary.completed, color: "#10b981" },
                            { name: "Pending", value: compAnalytics.summary.pending, color: "#f59e0b" },
                            { name: "Overdue", value: compAnalytics.summary.overdue, color: "#ef4444" },
                          ].filter(d => d.value > 0)} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value" paddingAngle={3} strokeWidth={0}>
                            {[
                              { color: "#10b981" }, { color: "#f59e0b" }, { color: "#ef4444" }
                            ].map((c, i) => <Cell key={i} fill={c.color} />)}
                          </Pie>
                          <RTooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="space-y-3 flex-1">
                        {[
                          { name: "Compliant", value: compAnalytics.summary.completed, color: "#10b981" },
                          { name: "Pending", value: compAnalytics.summary.pending, color: "#f59e0b" },
                          { name: "Overdue", value: compAnalytics.summary.overdue, color: "#ef4444" },
                        ].map((d, i) => (
                          <div key={i} className="flex items-center justify-between">
                            <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ background: d.color }} /><span className="text-sm text-muted-foreground">{d.name}</span></div>
                            <span className="text-sm font-bold">{d.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Dept Breakdown */}
                <Card>
                  <CardContent className="p-5">
                    <h3 className="text-sm font-bold mb-4 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-primary" /> Department Compliance</h3>
                    <div className="space-y-3">
                      {(compAnalytics.department_breakdown || []).map((dept, i) => (
                        <div key={i} className="space-y-1.5">
                          <div className="flex items-center justify-between text-sm">
                            <span className="font-medium">{dept.department}</span>
                            <span className="text-muted-foreground">{dept.avg_progress}% progress</span>
                          </div>
                          <div className="flex gap-1 h-2 rounded-full overflow-hidden bg-muted/50">
                            <div className="bg-emerald-500 rounded-full" style={{ width: `${(dept.completed / Math.max(dept.total, 1)) * 100}%` }} />
                            <div className="bg-amber-500 rounded-full" style={{ width: `${(dept.pending / Math.max(dept.total, 1)) * 100}%` }} />
                            <div className="bg-red-500 rounded-full" style={{ width: `${(dept.overdue / Math.max(dept.total, 1)) * 100}%` }} />
                          </div>
                          <div className="flex gap-3 text-[10px] text-muted-foreground">
                            <span>{dept.completed} compliant</span><span>{dept.pending} pending</span><span>{dept.overdue} overdue</span>
                          </div>
                        </div>
                      ))}
                      {(!compAnalytics.department_breakdown || compAnalytics.department_breakdown.length === 0) && <p className="text-sm text-muted-foreground text-center py-6">No department data</p>}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Course completion chart */}
              {(compAnalytics.course_analytics || []).length > 0 && (
                <Card>
                  <CardContent className="p-5">
                    <h3 className="text-sm font-bold mb-4 flex items-center gap-2"><BookOpen className="w-4 h-4 text-primary" /> Course Completion</h3>
                    <ResponsiveContainer width="100%" height={Math.max(150, compAnalytics.course_analytics.length * 50)}>
                      <BarChart data={compAnalytics.course_analytics.map(c => ({
                        name: c.title.length > 25 ? c.title.slice(0, 25) + "..." : c.title,
                        completed: c.completed, in_progress: c.in_progress, not_started: c.not_started,
                      }))} layout="vertical" margin={{ left: 10, right: 20 }}>
                        <XAxis type="number" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                        <YAxis type="category" dataKey="name" width={180} tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                        <RTooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                        <Bar dataKey="completed" stackId="a" fill="#10b981" />
                        <Bar dataKey="in_progress" stackId="a" fill="#3b82f6" />
                        <Bar dataKey="not_started" stackId="a" fill="#e2e8f0" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}
            </motion.div>
          )}

          {activeTab === "ai" && (
            <motion.div key="ai" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-6">
              {!aiInsights && !aiLoading && (
                <Card className="border-dashed border-2 border-primary/20">
                  <CardContent className="py-12 text-center">
                    <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4"><Brain className="w-8 h-8 text-primary" /></div>
                    <h3 className="text-lg font-bold mb-2">AI Compliance Analysis</h3>
                    <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">Analyze compliance risk, overdue patterns, department gaps, and get recommendations.</p>
                    <Button onClick={async () => {
                      setAiLoading(true);
                      try {
                        const res = await api.post(`/compliance/templates/${complianceId}/ai-insights`, {
                          analytics_summary: {
                            title: template.title,
                            category: template.category,
                            is_mandatory: template.is_mandatory,
                            total_assigned: assignments.length,
                            completed: completed.length,
                            overdue: overdue.length,
                            completion_rate: rate,
                            course_summary: compAnalytics?.course_analytics?.map(c => ({ title: c.title, completion_rate: c.completion_rate })) || [],
                            department_breakdown: compAnalytics?.department_breakdown || [],
                          }
                        });
                        setAiInsights(res.data);
                      } catch (e) { toast.error("Failed"); }
                      finally { setAiLoading(false); }
                    }} size="lg" data-testid="generate-ai-btn"><Sparkles className="w-5 h-5 mr-2" /> Generate AI Insights</Button>
                  </CardContent>
                </Card>
              )}
              {aiLoading && <Card><CardContent className="py-16 text-center"><Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-4" /><p className="text-sm text-muted-foreground">Analyzing compliance data...</p></CardContent></Card>}
              {aiInsights && !aiLoading && (
                <div className="space-y-6">
                  <Card className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white border-0">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-2 mb-3"><Brain className="w-5 h-5 text-primary" /><h3 className="text-sm font-bold text-white/70 uppercase tracking-wider">AI Compliance Summary</h3></div>
                      <p className="text-base leading-relaxed text-white/90">{aiInsights.summary}</p>
                    </CardContent>
                  </Card>
                  {aiInsights.insights?.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {aiInsights.insights.map((ins, i) => {
                        const sev = { critical: { bg: "bg-red-50", border: "border-red-200", text: "text-red-700", dot: "bg-red-500" }, warning: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", dot: "bg-amber-500" }, success: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", dot: "bg-emerald-500" }, info: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", dot: "bg-blue-500" } }[ins.severity] || { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", dot: "bg-blue-500" };
                        return (
                          <Card key={i} className={`${sev.bg} ${sev.border} border h-full`} data-testid={`ai-insight-${i}`}>
                            <CardContent className="p-4">
                              <div className="flex items-start gap-3">
                                <span className={`w-2.5 h-2.5 rounded-full ${sev.dot} mt-1.5 shrink-0`} />
                                <div><h4 className={`text-sm font-bold ${sev.text}`}>{ins.title}</h4><p className="text-xs text-muted-foreground mt-1">{ins.description}</p>{ins.metric && <span className={`inline-block mt-2 text-xs font-bold px-2 py-0.5 rounded-full ${sev.bg} ${sev.text}`}>{ins.metric}</span>}</div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                  {aiInsights.recommendations?.length > 0 && (
                    <Card><CardContent className="p-5">
                      <h3 className="text-sm font-bold mb-4 flex items-center gap-2"><Zap className="w-4 h-4 text-amber-500" /> AI Recommendations</h3>
                      <div className="space-y-3">{aiInsights.recommendations.map((rec, i) => (
                        <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-amber-50/50 border border-amber-100"><span className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center text-xs font-bold text-amber-700 shrink-0 mt-0.5">{i + 1}</span><p className="text-sm text-slate-700">{rec}</p></div>
                      ))}</div>
                    </CardContent></Card>
                  )}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Assignment Modal */}
      <AssignmentModal
        open={showAssign}
        onOpenChange={setShowAssign}
        title={template.title}
        onAssign={async (userIds) => {
          try {
            const res = await api.post(`/compliance/templates/${complianceId}/assign`, userIds);
            toast.success(res.data?.message || `Assigned to ${userIds.length} users`);
            fetchData();
          } catch (e) { toast.error("Failed to assign"); }
        }}
      />
    </AdminLayout>
  );
};

export default ComplianceDetailPage;
